import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { X } from 'lucide-react';

export interface ModalProps {
  isOpen?: boolean;
  open?: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEsc?: boolean;
  loading?: boolean;
  footer?: React.ReactNode;
  className?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
}

const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
  full: 'max-w-[calc(100vw-2rem)]',
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  open,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEsc = true,
  loading = false,
  footer,
  className,
  headerClassName,
  bodyClassName,
  footerClassName,
}) => {
  const visible = isOpen ?? open ?? false;

  useEffect(() => {
    if (!visible) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || !closeOnEsc) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [visible, closeOnEsc, onClose]);

  if (!visible) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
      onClick={closeOnBackdropClick ? onClose : undefined}
    >
      <div
        className={clsx(
          'flex max-h-[min(90dvh,900px)] w-full flex-col overflow-hidden rounded-panel border border-agentic-gray-200 bg-white shadow-floating',
          sizeClasses[size],
          className
        )}
        role="document"
        onClick={(event) => event.stopPropagation()}
      >
        {(title || showCloseButton) && (
          <div className={clsx('flex items-center justify-between border-b border-agentic-gray-200 px-6 py-4', headerClassName)}>
            {title ? (
              <h2 id="modal-title" className="text-lg font-semibold text-kv-agentic-ink">
                {title}
              </h2>
            ) : (
              <span />
            )}
            {showCloseButton ? (
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-agentic-text-secondary transition hover:bg-agentic-gray-100 hover:text-agentic-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kv-agentic-blue focus-visible:ring-offset-2"
                aria-label="Close dialog"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
        )}

        <div className={clsx('min-h-0 flex-1 overflow-y-auto px-6 py-5', bodyClassName)}>
          {loading ? (
            <div className="flex items-center gap-2 text-agentic-text-secondary">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-agentic-text-secondary border-t-transparent" />
              Loading...
            </div>
          ) : (
            children
          )}
        </div>

        {footer ? (
          <div className={clsx('flex items-center justify-end gap-3 border-t border-agentic-gray-200 px-6 py-4', footerClassName)}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
